import moment from "moment";
import ToggleButtonGroupComponent, { BUTTON_TYPE } from "../../../components/common/toggle-button-group.component";
import SingleSelectComponent from "../../../components/single-select/single-select.component";
import SelectSiteSearchInputComponent from "../../../components/site/select-site-search-input.component";
import { formats } from "../../../config/constants";
import StoreVisitReportModel from "../../../models/reports/store-visit/store-visit-report.model";
import ViewBase from "../../view-base.view";

/**
* Store Visit Report view
* 
* @author cmc.nttruong1
*/

export default function StoreVisitReportView() {
  ViewBase.call(this);

  const CURRENCY_TYPE = [
    { label: 'USD', value: '1' },
    { label: 'Local', value: '2' }
  ]

  const DEFAULT_FILTERS = {
    month: moment().format(formats.MONTH_FORMAT),
    site: null,
    bu: null,
    currency: CURRENCY_TYPE[0].value
  }

  let filters = structuredClone(DEFAULT_FILTERS);
  let currentFilter = {};
  let iframeHeight = 300;
  let spacingHeight = 40;// 40 is the total height of margin/padding/scrollbar. Adjust accordingly if style changes. 
  let htmlRaw = '';
  let listBUOptions = [];

  const toParams = () => {
    return {
      month: moment(currentFilter.month).format(formats.API_MONTH_FORMAT),
      siteId: currentFilter.site.siteId,
      bu: currentFilter.bu.code,
      currency: currentFilter.currency
    }
  }

  const search = (payload) => {
    this.showLoading();
    StoreVisitReportModel.search(payload)
      .then((res) => {
        htmlRaw = res || '';
      })
      .catch((error) => { this.handleError(error); })
      .finally(() => { this.hideLoading() })
  }

  const handleSearch = (e) => {
    if (!this.validateForm(e)) return false;

    currentFilter = structuredClone(filters);

    if (!e.target.checkValidity()) {
      showSiteSelectTooltip(!currentFilter.site);
      return;
    }

    search(toParams());
  }

  const handleClearFilters = () => {
    filters = structuredClone(DEFAULT_FILTERS);
  }

  const onSiteChanged = (value) => {
    listBUOptions = [];
    filters.bu = DEFAULT_FILTERS.bu;
    filters.site = value;

    if (value) {
      getListBUBySiteId(value.siteId);
    }
  }

  const onCurrencyChanged = (e) => {
    filters.currency = e.target.value;
  }

  const getListBUBySiteId = (siteId) => {
    StoreVisitReportModel.getListBU(siteId)
      .then((res) => {
        listBUOptions = res || [];
      })
  }

  const handleDownloadExcel = (e) => {
    e.preventDefault();
    this.showLoading();

    StoreVisitReportModel.exportExcel(toParams(), `StoreVislt_${currentFilter.site.siteId}_${currentFilter.bu.code}_${currentFilter.month}.xls`)
      .catch((error) => { this.handleError(error) })
      .finally(() => { this.hideLoading() })
  }

  return {
    view: () => {
      return [
        /* SEARCH */
        m(".d-flex.flex-column.align-items-end.gap-2.mb-3",
          m('.w-100',
            m(".card.mb-3",
              m(".card-body",
                m('form.needs-validation[novalidate].row.g-2.align-items-end', { onsubmit: handleSearch },
                  m(".col-md-4.col-lg-3.position-relative",
                    m("label.form-label[for=month].mandatory", this.getText("LT00000326", "Month")),
                    m("input#month.form-control[type=month][required]",
                      {
                        value: filters.month,
                        max: moment().format(formats.MONTH_FORMAT),
                        min: moment().subtract(36, 'month').format(formats.MONTH_FORMAT),
                        onchange: (e) => {
                          filters.month = e.target.value;
                        }
                      }
                    ),
                    m('.invalid-tooltip', this.getText("LT00000729", "This field is required")),
                  ),
                  m('.col-xl-3.col-lg-4.col-sm-6.position-relative',
                    m('label.form-label[for=site].mandatory', this.getText('LT00000071', 'Site')),
                    m(SelectSiteSearchInputComponent, {
                      required: true,
                      id: 'site',
                      selectedItem: filters.site,
                      onSelect: onSiteChanged
                    }),
                    m('.invalid-tooltip', this.getText("LT00000729", "This field is required")),
                  ),
                  m('.col-xl-3.col-lg-4.col-sm-6.position-relative',
                    m('label.form-label[for=bu].mandatory', this.getText('LT00000239', 'BU')),
                    m(SingleSelectComponent, {
                      disabled: listBUOptions.length == 0,
                      id: "bu",
                      items: listBUOptions,
                      selectedItem: filters.bu,
                      onSelect: (value) => {
                        filters.bu = value;
                      },
                      required: true,
                      allowEmpty: true,
                    }),
                    m('.invalid-tooltip', this.getText("LT00000729", "This field is required")),
                  ),
                  m('.col-xl-3.col-lg-4.col-sm-6.position-relative',
                    m('label.form-label', this.getText('LT00000282', 'Currency')),
                    m(ToggleButtonGroupComponent, {
                      ariaLabel: this.getText('LT00000282', 'Currency'),
                      buttonType: BUTTON_TYPE.RADIO,
                      buttons: CURRENCY_TYPE.map(type => ({
                        checked: filters.currency === type.value,
                        id: type.label,
                        label: type.label,
                        value: type.value,
                      })),
                      name: 'currency',
                      onChange: onCurrencyChanged,
                    }),
                  ),

                  // Search & clear buttons
                  m('.col-12.d-flex.justify-content-end.gap-2',
                    m('button.btn.btn-primary', this.getText("LT00000001", "Search")),
                    m('button.btn.btn-white[type=button]',
                      { onclick: handleClearFilters },
                      this.getText("LT00000031", "Clear")),
                  ),
                )
              )
            )
          )
        ),

        /* TABLE */
        m('.card',
          m('.card-body',
            m('.d-flex.justify-content-end.mb-2',
              m('.input-group.w-auto',
                m('button.btn.btn-primary',
                  {
                    disabled: !htmlRaw,
                    onclick: handleDownloadExcel
                  },
                  m('i.bi.bi-file-earmark-arrow-down'),
                  this.getText('LT00000492', 'Download')
                ),
              ),
            ),
            m('.row.g-2',
              m('.col-12',
                m('iframe', {
                  onload: (e) => {
                    iframeHeight = e.target.contentWindow.document.body.scrollHeight + spacingHeight;

                  },
                  id: 'preRenderedTable',
                  srcdoc: htmlRaw,
                  style: `width: 100%; height: ${iframeHeight}px;`,
                }),
              ),
            )
          )
        ),

        this.getBaseViews(),
      ];
    },
  };
}